import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Skeleton,
  Grid2,
  Button,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  LocalOffer,
  TrendingUp,
  CalendarToday,
  Assessment,
  CheckCircle,
  Cancel,
  Schedule,
  Receipt,
  Launch,
} from '@mui/icons-material';

import { PromoType } from '@libs/types';
import { routes } from '@carcareers/utils';
import { FormProvider } from '@carcareers/contexts';
import { DashboardPageContainer, InfoCard, SkeletonForm, PromoForm } from '@carcareers/components';
import { usePromoViewQuery } from '@carcareers/store';
import { usePromo } from '@carcareers/hooks';
import { useIsMobile } from '@carcareers/utils';

export const PromoView: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useIsMobile();
  const promoId = id ? parseInt(id) : undefined;
  const { data: promo, isLoading } = usePromoViewQuery(promoId ?? 0);
  const promoHandlers = usePromo();

  useEffect(() => {
    if (!promoId) {
      navigate(routes.ADMIN_PROMOS);
    }
  }, [promo, promoId, navigate, isLoading]);

  const handleActivate = async () => {
    if (!promoId) return;
    await promoHandlers.activatePromo(promoId);
  };

  const handleDeactivate = async () => {
    if (!promoId) return;
    await promoHandlers.deactivatePromo(promoId);
  };

  const getPromoStatus = () => {
    if (!promo) return { label: 'Unknown', color: 'default' as const, icon: Schedule };

    const isActive = promo.isActive;
    const now = new Date();
    const startDate = promo.startDate ? new Date(promo.startDate) : null;
    const endDate = promo.endDate ? new Date(promo.endDate) : null;

    if (endDate && now > endDate) {
      return { label: 'Expired', color: 'error' as const, icon: Cancel };
    }

    if (startDate && now < startDate) {
      return { label: 'Not Started', color: 'warning' as const, icon: Schedule };
    }

    if (isActive) {
      return { label: 'Active', color: 'success' as const, icon: CheckCircle };
    } else {
      return { label: 'Inactive', color: 'default' as const, icon: Cancel };
    }
  };

  const getUsagePercentage = () => {
    if (!promo || !promo.maxUses) return 0;
    return Math.round(((promo.uses || 0) / promo.maxUses) * 100);
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error.main';
    if (percentage >= 70) return 'warning.main';
    return 'success.main';
  };

  const renderDiscountValue = () => {
    if (!promo) return 'N/A';
    const discountValue = promo.discount || 0;
    const discountType = promo.type || PromoType.PERCENTAGE;
    return discountType === PromoType.PERCENTAGE ? `${discountValue}%` : `$${discountValue}`;
  };

  const status = getPromoStatus();
  const usagePercentage = getUsagePercentage();

  return (
    <DashboardPageContainer idReplacement={promo?.code}>
      <Stack spacing={3}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                <LocalOffer sx={{ fontSize: 40 }} />
              </Avatar>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {isLoading ? <Skeleton variant="text" width={200} height={32} /> : promo?.code || 'Unknown Promo'}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {isLoading ? (
                    <Skeleton variant="text" width={150} height={20} />
                  ) : (
                    `${renderDiscountValue()} discount`
                  )}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<status.icon />}
                    label={status.label}
                    color={status.color}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                  <Chip icon={<Assessment />} label={`ID: ${promo?.id || '...'}`} variant="outlined" sx={{ px: 1 }} />
                  <Chip
                    icon={<TrendingUp />}
                    label={`Usage: ${usagePercentage}%`}
                    variant="outlined"
                    sx={{
                      px: 1,
                      color: getUsageColor(usagePercentage),
                      borderColor: getUsageColor(usagePercentage),
                    }}
                  />
                  {promo?.startDate && (
                    <Chip
                      icon={<CalendarToday />}
                      label={`Created ${new Date(promo.startDate).toLocaleDateString()}`}
                      variant="outlined"
                      sx={{ px: 1 }}
                    />
                  )}
                </Stack>
              </Stack>
              <Stack direction={isMobile ? 'row' : 'column'} spacing={1}>
                {promo?.isActive ? (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeactivate}
                    startIcon={<Cancel />}
                    size="small"
                  >
                    Deactivate
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleActivate}
                    startIcon={<CheckCircle />}
                    size="small"
                  >
                    Activate
                  </Button>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <TrendingUp sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : promo?.uses || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Times Used
              </Typography>
            </CardContent>
          </Card>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Assessment sx={{ color: 'success.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : promo?.maxUses || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Max Uses
              </Typography>
            </CardContent>
          </Card>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'warning.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Receipt sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
                {isLoading ? <Skeleton variant="text" width={40} height={32} /> : promo?.invoices?.length || 0}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Invoices
              </Typography>
            </CardContent>
          </Card>
        </Stack>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Discount Amount" value={renderDiscountValue()} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard
              label="Discount Type"
              value={promo?.type === PromoType.PERCENTAGE ? 'Percentage' : 'Fixed Amount'}
              loading={isLoading}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Is Active" value={promo?.isActive ? 'Yes' : 'No'} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <InfoCard label="Usage Rate" value={`${usagePercentage}%`} loading={isLoading} />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard
              label="Start Date"
              value={promo?.startDate ? new Date(promo.startDate).toLocaleDateString() : 'Not set'}
              loading={isLoading}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <InfoCard
              label="End Date"
              value={promo?.endDate ? new Date(promo.endDate).toLocaleDateString() : 'Not set'}
              loading={isLoading}
            />
          </Grid2>
        </Grid2>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalOffer sx={{ color: 'primary.main' }} />
                Promo Details
              </Typography>
            </Box>
            <Box
              sx={{
                p: 3,
                '& form': {
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                },
              }}
            >
              <FormProvider initialData={promo}>
                {isLoading ? <SkeletonForm /> : <PromoForm type="update" />}
              </FormProvider>
            </Box>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt sx={{ color: 'primary.main' }} />
                Associated Invoices
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              {!promo?.invoices || promo.invoices.length === 0 ? (
                <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No invoices associated with this promo code
                </Typography>
              ) : (
                <List>
                  {promo.invoices.map((invoice) => (
                    <ListItem
                      key={invoice.id}
                      divider
                      secondaryAction={
                        <Button
                          size="small"
                          endIcon={<Launch />}
                          onClick={() => navigate(`/admin/invoice/${invoice.id}`)}
                        >
                          View
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={`Invoice #${invoice.id}`}
                        secondary={
                          <Typography component="span" variant="body2" color="text.primary">
                            {invoice.totalPaymentDue && `Amount: $${invoice.totalPaymentDue}`}
                            {invoice.createdAt && ` • Created: ${new Date(invoice.createdAt).toLocaleDateString()}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </DashboardPageContainer>
  );
};

export default PromoView;
